import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { DropdownItem, DropdownMenu } from 'reactstrap';

import { IRootState } from '@app/store';
import profileMenus from '@config/profile-menu';
import { logoutRequest, clearUserTokenRequest } from '@reducers/auth/AuthAction';
import { cancelAllAxiosRequests, startLogout, finishLogout } from '@services/AxiosConfig';
import LocaleService from '@services/LocaleService';
import MessagingService from '@services/MessagingService';
// import { handleError } from '@services/ErrorHandler';
import {
// EditProfileIcon,
  ProfileIcon,
  SignOutIcon,
} from '@shared/icons';

import './ProfileMenu.scss';

// Define a type for user type list
type UserTypeList = {
  dentist: string;
  'dental-clinic': string;
  laboratory: string;
};

// Define UserType with a union of keys of UserTypeList
type UserType = keyof UserTypeList;

const ProfileMenu = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const userDetails = useSelector((state: IRootState) => state.Auth?.user);
  const invitesCount = useSelector(({ Dentist }) => Dentist.myClinicInvites);
  const newDentistsCount = useSelector(({ Clinic }) => Clinic.myNewDentistsCount);
  const userType = useSelector((state: IRootState) => state.Auth?.user?.type) as UserType;
  const userName = userDetails?.name || userDetails?.full_name;
  const userImage = userDetails?.image;
  const isVerify = location.pathname.endsWith('verify');
  const i18n = LocaleService.getTranslations('navigation');
  const userProfileMenus = userType ? profileMenus[userType] : [];

  const PROFILE = [
    {
      title: userName,
      icon: !userImage ? <ProfileIcon />
        : (
          <span className="profile-menu-user-image-container">
            <img
              className="user-profile"
              src={userImage}
              alt="User Profile"
            />
          </span>
        ),
      key: 'user_profile',
    },
    ...userProfileMenus,
  ];

  const handleLogout = async () => {
    // clear the messaging instance once the user logs out, to avoid
    // 401 issue in broadcasting when changing accounts (re-initialize getInstance())
    MessagingService.clearInstance();
    startLogout();
    cancelAllAxiosRequests();
    try {
      await dispatch(logoutRequest()).$promise;
    } catch (error) {
      // handleError(error);
      dispatch(clearUserTokenRequest());
    } finally {
      finishLogout();
    }
  };

  const handleOnClickItem = (item: string) => {
    switch (item) {
      case 'edit_profile': {
        navigate(`/profile/${userDetails?.id}`);
        break;
      }
      case 'my_dentists': {
        navigate('/my-dentists/dentists');
        break;
      }
      case 'notification_settings': {
        navigate('/notification-settings');
        break;
      }
      case 'my_clinics': {
        navigate('/my-clinics/affiliations');
        break;
      }
      case 'community_board': {
        navigate('/community-board');
        break;
      }
      case 'my_plans': {
        navigate('/plans-payments');
        break;
      }
      case 'my_packages': {
        navigate('/packages-pricing');
        break;
      }
      case 'tutorial_videos': {
        navigate('/tutorials');
        break;
      }
      case 'sign_out': {
        handleLogout();
        break;
      }
      default:
    }
  };

  return (
    <DropdownMenu className="profile-menu" end>
      {isVerify ? (
        <DropdownItem
          className="only-dropdown-item"
          key="sign_out"
          onClick={() => handleOnClickItem('sign_out')}
        >
          <SignOutIcon />
          <span className="b1">{i18n.button.sign_out}</span>
        </DropdownItem>
      ) : (
        PROFILE.map((item, index) => (
          <DropdownItem
            className={classNames('dropdown-item', {
              'first-dropdown-item': index === 0 && PROFILE.length > 1,
              'last-dropdown-item': (index === PROFILE.length - 1 && PROFILE.length > 2) || item.key === 'my_plans',
            })}
            key={item.key}
            onClick={() => handleOnClickItem(item.key)}
          >
            {item.icon}
            <span className="b1">{item.title}</span>
            {invitesCount > 0 && item.key === 'my_clinics' && (
              <span className="unread-count">
                <div>{invitesCount}</div>
              </span>
            )}
            {newDentistsCount > 0 && item.key === 'my_dentists' && (
              <span className="unread-count">
                <div>{newDentistsCount}</div>
              </span>
            )}
          </DropdownItem>
        ))
      )}
    </DropdownMenu>
  );
};

export default ProfileMenu;
